import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@vtbhome/juicyfront';
import { FeedbackRequestChangeStatusProps } from './types';
import { Toast } from '@shared/ui/toast/toast';
import toast from 'react-hot-toast';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useFeedbackRequestChangeStatusMutation } from '@entities/feedback-request/api/feedback-request-api';
import { CACHE_KEY_FEEDBACK_REQUEST_CHANGE_STATUS } from '@entities/feedback-request/config/api-constants';
import { FEEDBACK_REQUEST_ICON_STATUS_MAP, FEEDBACK_REQUEST_TEXT_STATUS_MAP } from '../config/ui-constants';
import { FeedbackRequestStatus } from '@shared/model/feedback-request';
import { ModalCompleteFeedbackRequest } from './modal-complete-feedback-request/modal-complete-feedback-request';
import {
    FEEDBACK_REQUEST_NEXT_STEP_STATUS_MAP,
    FEEDBACK_REQUEST_SUCCESS_MESSAGE_STATUS_MAP,
    FEEDBACK_REQUEST_ERROR_MESSAGE_STATUS_MAP,
} from '@entities/feedback-request/config/ui-constants';

export const FeedbackRequestChangeStatus: React.FC<FeedbackRequestChangeStatusProps> = ({
    feedbackRequest,
    onSuccess,
}) => {
    const [changeFeedbackRequestStatus, feedbackRequestChangeStatusResult] = useFeedbackRequestChangeStatusMutation({
        fixedCacheKey: CACHE_KEY_FEEDBACK_REQUEST_CHANGE_STATUS,
    });

    const [isShowModal, setIsShowModal] = useState(false);

    const handleClose = () => {
        setIsShowModal(false);
    };

    const handleFeedbackRequestChangeStatus = (status: FeedbackRequestStatus) => {
        if (status === 'completed') {
            return setIsShowModal(true);
        }

        changeFeedbackRequestStatus({
            id: feedbackRequest.id,
            status,
        });
    };

    const stopEvent = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.stopPropagation();
    };

    const nextStatus = useMemo(
        () => FEEDBACK_REQUEST_NEXT_STEP_STATUS_MAP[feedbackRequest.status],
        [feedbackRequest.status]
    );

    useEffect(() => {
        if (!nextStatus || feedbackRequestChangeStatusResult.data?.id !== feedbackRequest.id) {
            return;
        }
        if (isShowModal && feedbackRequestChangeStatusResult.isSuccess) {
            onSuccess?.();
            toast.custom((t) => {
                return (
                    <Toast
                        type='success'
                        title='Успешно!'
                        description={FEEDBACK_REQUEST_SUCCESS_MESSAGE_STATUS_MAP[nextStatus]}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            feedbackRequestChangeStatusResult.reset();
        } else if (isShowModal && feedbackRequestChangeStatusResult.isError) {
            toast.custom((t) => {
                return (
                    <Toast
                        type='error'
                        title='Ошибка!'
                        description={`${FEEDBACK_REQUEST_ERROR_MESSAGE_STATUS_MAP[nextStatus]}.\n${getQueryError(feedbackRequestChangeStatusResult)?.message}`}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            feedbackRequestChangeStatusResult.reset();
        }
    }, [
        isShowModal,
        nextStatus,
        feedbackRequestChangeStatusResult.isSuccess,
        feedbackRequestChangeStatusResult.isError,
        feedbackRequestChangeStatusResult.data?.id,
    ]);

    if (!nextStatus) return null;

    return (
        <>
            <Button
                buttonType='secondary'
                size='s'
                preloader={feedbackRequestChangeStatusResult.isLoading}
                startAdornment={FEEDBACK_REQUEST_ICON_STATUS_MAP[feedbackRequest.status]}
                onClick={(e) => {
                    stopEvent(e);
                    handleFeedbackRequestChangeStatus(nextStatus);
                }}>
                {FEEDBACK_REQUEST_TEXT_STATUS_MAP[feedbackRequest.status]}
            </Button>
            <div onClick={stopEvent}>
                <ModalCompleteFeedbackRequest
                    isShow={isShowModal}
                    feedbackRequestId={feedbackRequest.id}
                    onAccept={handleClose}
                    onClose={handleClose}
                />
            </div>
        </>
    );
};
